import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { GrSearch } from 'react-icons/gr';
import { FiPlusCircle } from 'react-icons/fi';
import moment from 'moment';
import adminServices from '../../services/AdminServices';
import FetchError from '../feedback/FetchError';
import Pagination from '../nav/Pagination';
import Empty from '../feedback/Empty';
import Loading from '../feedback/Loading';
import RequestModal from '../requests/RequestModal';
import utils from '../../utils';

function PatientRequestsList({ patientId }) {
    const [searchParams, setSearchParams] = useSearchParams();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState();
    const [requests, setRequests] = useState();
    const [action, setAction] = useState(0);
    const [activeRequest, setActiveRequest] = useState(null);

    const searchInputRef = useRef();
    const navigate = useNavigate();
    const location = useLocation();

    const currentStatus = searchParams.get('status') || '';
    const currentUrgency = searchParams.get('urgency_level') || '';
    const currentOrdering = searchParams.get('ordering') || '';
    const currentSearch = searchParams.get('search') || '';

    useEffect(() => {
        const params = new URLSearchParams(searchParams);
        if (patientId) {
            params.set('patient', patientId);
        }
        getRequests(params.toString());
    }, [searchParams, patientId]);

    useEffect(() => { }, [activeRequest]);

    const getRequests = (params) => {
        setError();
        setLoading(true);
        adminServices.getRequests(params ? `?${params}` : '')
            .then(res => {
                setRequests(res.data);
                setLoading(false);
            })
            .catch(err => {
                console.error(err);
                setError(err);
                setLoading(false);
            });
    };

    const handleSearch = (e) => {
        if (e.key === 'Enter') {
            const query = e.target.value.trim();
            setSearchParams(prev => {
                const newParams = new URLSearchParams(prev);
                if (query) {
                    newParams.set('search', query);
                } else {
                    newParams.delete('search');
                }
                newParams.delete('page');
                return newParams;
            });
        }
    };

    const updateURL = (newParams) => {
        navigate({
            pathname: location.pathname,
            search: newParams.toString(),
        }, { replace: true });
    };

    const handleStatusChange = (e) => {
        const value = e.target.value;
        const params = new URLSearchParams(searchParams);
        if (value) {
            params.set('status', value);
        } else {
            params.delete('status');
        }
        params.delete('page');
        updateURL(params);
    };

    const handleUrgencyChange = (e) => {
        const value = e.target.value;
        const params = new URLSearchParams(searchParams);
        if (value) {
            params.set('urgency_level', value);
        } else {
            params.delete('urgency_level');
        }
        params.delete('page');
        updateURL(params);
    };

    const handleOrderingChange = (e) => {
        const value = e.target.value;
        const params = new URLSearchParams(searchParams);
        if (value) {
            params.set('ordering', value);
        } else {
            params.delete('ordering');
        }
        params.delete('page');
        updateURL(params);
    };

    const changePageParam = (page) => {
        setSearchParams(prev => {
            const newParams = new URLSearchParams(prev);
            if (page) {
                newParams.set('page', page);
            } else {
                newParams.delete('page');
            }
            return newParams;
        });
    };

    function openModal(action, request = null) {
        const openBtn = document.getElementById("request-create-open-btn");
        setAction(action);
        setActiveRequest(request);
        if (openBtn) openBtn.click();
    }

    if (!patientId) return null;

    return (
        <div className="space-y-5">
            {/* <RequestStats patientId={patientId} /> */}

            <RequestModal
                action={action}
                activeRequest={activeRequest}
                onReload={() => {
                    const params = new URLSearchParams(searchParams);
                    params.set('patient', patientId);
                    getRequests(params.toString());
                }}
                setActiveRequest={setActiveRequest}
            />

            <div className="bg-surface-light p-4 rounded-xl border border-border-light">
                <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
                    <div className="w-full md:w-auto">
                        <label className="sr-only" htmlFor="request-search">Search</label>
                        <div className="relative">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <GrSearch className="text-zinc-400 text-[20px]" />
                            </div>
                            <input
                                className="block w-full pl-10 pr-3 py-2.5 border border-border-light rounded-lg bg-zinc-50 text-zinc-900 placeholder-zinc-500 focus:ring-1 focus:ring-primary focus:border-primary sm:text-sm"
                                id="request-search"
                                name="request-search"
                                placeholder="Search by nurse, skill, or location"
                                type="text"
                                defaultValue={currentSearch}
                                onKeyDown={handleSearch}
                                ref={searchInputRef}
                            />
                        </div>
                    </div>

                    <div className="flex flex-col md:flex-row gap-4">
                        <div>
                            <div className="relative">
                                <select
                                    className="block w-full pl-3 pr-10 py-2.5 text-base border-border-light focus:outline-none focus:ring-primary focus:border-primary sm:text-sm rounded-lg bg-zinc-50 text-zinc-700 appearance-none border"
                                    value={currentStatus}
                                    onChange={handleStatusChange}
                                >
                                    <option value="">All Statuses</option>
                                    <option value="Pending">Pending</option>
                                    <option value="Matched">Matched</option>
                                    <option value="Accepted">Accepted</option>
                                    <option value="Cancelled">Cancelled</option>
                                    <option value="Completed">Completed</option>
                                </select>
                                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-zinc-500">
                                    <FiPlusCircle className="text-[20px]" />
                                </div>
                            </div>
                        </div>

                        <div>
                            <div className="relative">
                                <select
                                    className="block w-full pl-3 pr-10 py-2.5 text-base border-border-light focus:outline-none focus:ring-primary focus:border-primary sm:text-sm rounded-lg bg-zinc-50 text-zinc-700 appearance-none border"
                                    value={currentUrgency}
                                    onChange={handleUrgencyChange}
                                >
                                    <option value="">All Urgency</option>
                                    <option value="Low">Low</option>
                                    <option value="Medium">Medium</option>
                                    <option value="Urgent">Urgent</option>
                                </select>
                                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-zinc-500">
                                    <FiPlusCircle className="text-[20px]" />
                                </div>
                            </div>
                        </div>

                        <div>
                            <div className="relative">
                                <select
                                    className="block w-full pl-3 pr-10 py-2.5 text-base border-border-light focus:outline-none focus:ring-primary focus:border-primary sm:text-sm rounded-lg bg-zinc-50 text-zinc-700 appearance-none border"
                                    value={currentOrdering}
                                    onChange={handleOrderingChange}
                                >
                                    <option value="">Sort By</option>
                                    <option value="-creation_time">Newest First</option>
                                    <option value="creation_time">Oldest First</option>
                                    <option value="-last_updated_time">Recently Updated</option>
                                    <option value="urgency_level">Urgency (A-Z)</option>
                                    <option value="-urgency_level">Urgency (Z-A)</option>
                                </select>
                                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-zinc-500">
                                    <FiPlusCircle className="text-[20px]" />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {error && <FetchError error={error} />}

            {loading ? (
                <Loading />
            ) : (
                <div>
                    <div className="bg-surface-light dark:bg-surface-dark rounded-xl border border-border-light dark:border-border-dark overflow-hidden">
                        <div className='overflow-x-auto'>
                            <table className="table table-compact table-zebra w-full">
                                <thead>
                                    <tr>
                                        <th>Nurse</th>
                                        <th className='hidden md:table-cell'>Location</th>
                                        <th className='hidden md:table-cell'>Urgency</th>
                                        <th className='hidden md:table-cell'>Care Needs</th>
                                        <th className='hidden md:table-cell'>Created</th>
                                        <th>Status</th>
                                        <th>Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {requests?.results?.length > 0 ? (
                                        requests.results.map((request) => (
                                            <tr key={request.id}>
                                                <td>
                                                    {request.get_assigned_nurse ? (
                                                        <Link to={`/nurse/${request.get_assigned_nurse.nurse?.id}`} className='flex gap-3 items-center group hover:cursor-pointer'>
                                                            <div className="hidden md:avatar placeholder">
                                                                {request.get_assigned_nurse.nurse?.user?.image ? (
                                                                    <div className="w-12 rounded-full">
                                                                        <img
                                                                            src={request.get_assigned_nurse.nurse.user.image}
                                                                            alt={request.get_assigned_nurse.nurse.user.full_name}
                                                                        />
                                                                    </div>
                                                                ) : (
                                                                    <div className="bg-neutral text-white rounded-full w-12 flex items-center justify-center">
                                                                        <span className="text-sm">
                                                                            {utils.makeProfileLetter(request.get_assigned_nurse.nurse.user)}
                                                                        </span>
                                                                    </div>
                                                                )}
                                                            </div>
                                                            <div>
                                                                <p className="font-bold group-hover:underline text-sm">
                                                                    {request.get_assigned_nurse.nurse.user.full_name}
                                                                </p>
                                                                <p className="text-gray-600 text-xs">
                                                                    {request.get_assigned_nurse.nurse.user.phone_number || request.get_assigned_nurse.nurse.user.email}
                                                                </p>
                                                            </div>
                                                        </Link>
                                                    ) : <span className="text-gray-400 text-sm">— Unassigned</span>}
                                                </td>
                                                <td className='hidden md:table-cell text-sm'>
                                                    {request.location_preference?.address || '—'}
                                                </td>
                                                <td className='hidden md:table-cell'>
                                                    <span className={`px-2 py-0.5 rounded text-xs ${request.urgency_level === 'Urgent' ? 'bg-red-100 text-red-800' :
                                                            request.urgency_level === 'Medium' ? 'bg-yellow-100 text-yellow-800' :
                                                                'bg-green-100 text-green-800'
                                                        }`}>
                                                        {request.urgency_level || '—'}
                                                    </span>
                                                </td>
                                                <td className='hidden md:table-cell text-sm'>
                                                    {(() => {
                                                        const skills = request.requested_skills || [];
                                                        if (skills.length === 0) return '—';
                                                        const displayed = skills.slice(0, 2).map(s => s.name);
                                                        return skills.length > 2 ? displayed.join(', ') + ', ...' : displayed.join(', ');
                                                    })()}
                                                </td>
                                                <td className='hidden md:table-cell text-sm text-gray-500'>
                                                    {moment(request.creation_time).format("MMM Do YYYY")}
                                                </td>
                                                <td>
                                                    <span className={`px-2 py-1 rounded text-xs ${request.status === 'Pending' ? 'bg-yellow-100 text-yellow-800' :
                                                            request.status === 'Matched' ? 'bg-blue-100 text-blue-800' :
                                                                request.status === 'Accepted' ? 'bg-purple-100 text-purple-800' :
                                                                    request.status === 'Completed' ? 'bg-green-100 text-green-800' :
                                                                        'bg-red-100 text-red-800'
                                                        }`}>
                                                        {request.status}
                                                    </span>
                                                </td>
                                                <td>
                                                    <button
                                                        onClick={() => openModal(1, request)}
                                                        className="btn btn-secondary btn-sm">
                                                        View
                                                    </button>
                                                </td>
                                            </tr>
                                        ))
                                    ) : null}
                                </tbody>
                            </table>

                            {(!requests || requests.results?.length === 0) && !loading && (
                                <div className="p-3">
                                    <Empty objectName="Requests" />
                                </div>
                            )}
                        </div>
                    </div>

                    {!loading && !error && requests && requests.count > 0 && (
                        <div className="flex justify-center mt-7">
                            <Pagination
                                count={requests.count}
                                next={requests.next}
                                previous={requests.previous}
                                changePageParam={changePageParam}
                            />
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

export default PatientRequestsList;